import {getReliableActualDoses} from '@/modules/preparation-label/build-label-data'
import {createLabelFormat, type LabelFormat} from '@/modules/preparation-label/format'
import type {ComponentRow, PreparationReportInput, ReportBatch, ReportTranslate, ReportWeighing} from './types'

const NO_VALUE = '—'

type VialWeighing = ReportWeighing & {batchId: string; medicineId: string}

function isVialWeighing(weighing: ReportWeighing): weighing is VialWeighing {
  return weighing.step === 'DRUG' && Boolean(weighing.batchId) && Boolean(weighing.medicineId)
}

function quantityOf(weighing: ReportWeighing) {
  const {allocatedDose, expectedWeightGrams: expected, actualWeightGrams: actual} = weighing
  if (allocatedDose == null || expected == null || expected <= 0 || actual <= 0) return null
  return (allocatedDose * actual) / expected
}

function formatMemoryNumber(memoryNumber: number | null | undefined) {
  return memoryNumber != null ? String(memoryNumber).padStart(4, '0') : NO_VALUE
}

/**
 * One row per weighed vial (DRUG weighings that know their batch), numbered per medicine,
 * then the carrier. Productions recorded before batch ids were stored, volumetric ones and
 * top-up ones fall back to one row per picked vial batch.
 */
export function buildComponentRows(input: PreparationReportInput, t: ReportTranslate): ComponentRow[] {
  const {label, weighings} = input
  const format = createLabelFormat(label.settings.language)
  const vialWeighings = weighings.filter(isVialWeighing).toSorted((a, b) => a.substanceIndex - b.substanceIndex)
  const drugRows = vialWeighings.length
    ? vialRows(input, vialWeighings, format, t)
    : input.vialBatches.map(batch => fallbackRow(input, batch, t))

  const solventComponentRows = solventRows(input, vialWeighings, format, t)

  const carrierMemory = weighings.find(weighing => weighing.step === 'CARRIER_BASELINE')?.scaleMemoryNumber
  const carrierRows: ComponentRow[] = input.carrierBatches.map(batch => ({
    type: t('preparation_report.type_carrier'),
    tradeName: batch.tradeName?.name ?? label.carrier?.name ?? NO_VALUE,
    productCode: batch.tradeName?.nationalId ?? NO_VALUE,
    substance: t('preparation_report.carrier_solution'),
    batchUsage: batch.lotNumber ?? NO_VALUE,
    quantity: label.carrier?.volumeMl ? `${format.dose(label.carrier.volumeMl)} ml` : NO_VALUE,
    memoryNumber: formatMemoryNumber(carrierMemory),
  }))

  return [...drugRows, ...solventComponentRows, ...carrierRows]
}

/**
 * One row per solvent row of each reconstituted vial, with the volume the cleanroom instruction asked for.
 * The lot is only known when a solvent batch was picked (not when the solvent stock check is skipped); the
 * memory number is the n-th SOLVENT weighing of the vial's substance, n being the solvent row's position.
 */
function solventRows(
  input: PreparationReportInput,
  vialWeighings: VialWeighing[],
  format: LabelFormat,
  t: ReportTranslate
): ComponentRow[] {
  const batchById = new Map(input.vialBatches.map(batch => [batch.id, batch]))
  const sources = vialWeighings.length
    ? vialWeighings.flatMap(weighing => {
        const batch = batchById.get(weighing.batchId)
        return batch ? [{batch, substanceIndex: weighing.substanceIndex as number | null}] : []
      })
    : input.vialBatches.map(batch => ({batch, substanceIndex: null as number | null}))

  const solventWeighings = new Map<number, ReportWeighing[]>()
  for (const weighing of input.weighings) {
    if (weighing.step !== 'SOLVENT') continue
    solventWeighings.set(weighing.substanceIndex, [...(solventWeighings.get(weighing.substanceIndex) ?? []), weighing])
  }

  const lotBySolvent = new Map<string, string | null>()
  for (const batch of input.solventBatches) {
    if (batch.drugCatalogItemId && !lotBySolvent.has(batch.drugCatalogItemId))
      lotBySolvent.set(batch.drugCatalogItemId, batch.lotNumber)
  }

  return sources.flatMap(({batch, substanceIndex}) =>
    batch.reconstitutionSolvents.map((solvent, position) => ({
      type: t('preparation_report.type_solvent'),
      tradeName: solvent.name || NO_VALUE,
      productCode: NO_VALUE,
      substance: t('preparation_report.reconstitution_solvent'),
      batchUsage: lotBySolvent.get(solvent.solutionDrugId) ?? NO_VALUE,
      quantity: `${format.dose(solvent.volumeMl)} ml`,
      memoryNumber: formatMemoryNumber(
        substanceIndex === null ? null : solventWeighings.get(substanceIndex)?.[position]?.scaleMemoryNumber
      ),
    }))
  )
}

/**
 * One cleanroom draw covers every vial picked from a batch, so a row stands for a batch
 * draw: the vials used are the drawn dose over one vial's content, rounded up — the same
 * split into full and partial vials the cleanroom allocation makes.
 */
function vialsUsed(weighing: VialWeighing, batch: ReportBatch | undefined) {
  if (!batch?.vialDose || weighing.allocatedDose == null) return null
  return Math.max(1, Math.ceil(weighing.allocatedDose / batch.vialDose - 1e-9))
}

function vialRows(
  input: PreparationReportInput,
  vialWeighings: VialWeighing[],
  format: LabelFormat,
  t: ReportTranslate
): ComponentRow[] {
  const substanceById = new Map(input.label.substances.map(substance => [substance.medicineId, substance]))
  const batchById = new Map(input.vialBatches.map(batch => [batch.id, batch]))

  return vialWeighings.map(weighing => {
    const batch = batchById.get(weighing.batchId)
    const substance = substanceById.get(weighing.medicineId)
    const quantity = quantityOf(weighing)
    const vials = vialsUsed(weighing, batch)
    const lot = batch?.lotNumber ?? NO_VALUE
    return {
      type: t('preparation_report.type_drug'),
      tradeName: batch?.tradeName?.name ?? NO_VALUE,
      productCode: batch?.tradeName?.nationalId ?? NO_VALUE,
      substance: substance?.name ?? NO_VALUE,
      batchUsage: vials === null ? lot : `${lot} · ${t('preparation_report.vials', {count: vials})}`,
      quantity:
        quantity === null
          ? NO_VALUE
          : `${format.dose(Math.round(quantity * 10) / 10)} ${substance ? format.unit(substance.doseUnit) : ''}`.trim(),
      memoryNumber: formatMemoryNumber(weighing.scaleMemoryNumber),
    }
  })
}

function fallbackRow(input: PreparationReportInput, batch: ReportBatch, t: ReportTranslate): ComponentRow {
  const {substances} = input.label
  // without per-vial weighings the substance is only certain for single-substance preparations
  const substance = substances.length === 1 ? substances[0].name : NO_VALUE
  return {
    type: t('preparation_report.type_drug'),
    tradeName: batch.tradeName?.name ?? NO_VALUE,
    productCode: batch.tradeName?.nationalId ?? NO_VALUE,
    substance,
    batchUsage: batch.lotNumber ?? NO_VALUE,
    quantity: t('preparation_report.units', {count: input.vialBatchQuantities[batch.id] ?? 1}),
    memoryNumber: NO_VALUE,
  }
}

/** one line per prescribed medicine: weighed total and deviation, or the prescribed amount when not reliable */
export function buildComponentTotals(input: PreparationReportInput, t: ReportTranslate): string[] {
  const reliable = getReliableActualDoses(input.label)
  const format = createLabelFormat(input.label.settings.language)
  return input.label.substances.map(substance => {
    const name = substance.tradeName ?? substance.name
    const actual = reliable.get(substance.medicineId)
    if (actual === undefined) {
      return `${t('preparation_report.total_for', {name})}: ${format.dose(substance.prescribedDose)} ${format.unit(substance.doseUnit)}`
    }
    return [
      `${t('preparation_report.total_for', {name})}: ${format.oneDecimal(actual)} ${format.unit(substance.doseUnit)}`,
      `${t('preparation_report.deviation')}: ${format.percentDifference(actual, substance.prescribedDose)}`,
    ].join('      ')
  })
}
